/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';

import Modal from '../../../../components/Modal/Modal';
import NonCompletionReasonPicker from '../../../../components/NonCompletionReasonPicker';
import {
   mapFieldErrors,
   firstErrorCode,
} from '../../../../services/routing/routingScheduleApi';

// =============================================================================
// EventConfirmDialog — confirmação de registro de evento (handoff v1 §6.3).
//
// Decisão de produto: TODO botão de evento (intermediário ou terminal) abre
// este dialog. Premissa "verifique antes de confirmar" — a v1 não expõe
// correção de evento na UI.
//
//   - event_timestamp default = agora (editável; backend rejeita >5min no
//     futuro com 422 em event_timestamp).
//   - CANCELLED exige non_completion_reason_id (validado aqui E no backend).
//   - 422 do backend mapeia para erro por campo via mapFieldErrors; mensagens
//     sem campo correspondente caem no aviso geral.
// =============================================================================

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
`;

const SummaryLine = styled.div`
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};

   strong {
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
      font-weight: 600;
   }
`;

const WarnBox = styled.div`
   background: rgba(211, 47, 47, 0.08);
   border: 1px solid rgba(211, 47, 47, 0.4);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.8rem;
   color: #b71c1c;
   line-height: 1.5;
`;

const OverrideBox = styled(WarnBox)`
   background: rgba(245, 158, 11, 0.08);
   border-color: rgba(245, 158, 11, 0.5);
   color: #92400e;
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;

   label {
      font-size: 0.75rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }

   input,
   textarea {
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid
         ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-family: inherit;
   }

   textarea {
      min-height: 64px;
      resize: vertical;
   }
`;

const FieldError = styled.span`
   font-size: 0.7rem;
   color: #c62828;
`;

// "YYYY-MM-DDTHH:mm" local — formato do input datetime-local.
function nowLocalInputValue() {
   const d = new Date();
   const pad = (n) => String(n).padStart(2, '0');
   return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
   )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Converte o valor do datetime-local em ISO-8601 com offset do browser
// (ex: 2026-06-11T14:30:00-03:00) — formato exigido pelo backend.
function toIsoWithOffset(localValue) {
   const d = new Date(localValue);
   if (Number.isNaN(d.getTime())) return null;
   const pad = (n) => String(n).padStart(2, '0');
   const offsetMinutes = -d.getTimezoneOffset();
   const sign = offsetMinutes >= 0 ? '+' : '-';
   const abs = Math.abs(offsetMinutes);
   return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
      `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
   );
}

export default function EventConfirmDialog({
   isOpen,
   onClose,
   stop = null,
   kindDef = null,
   isTerminalOverride = false,
   onSubmit,
}) {
   const [timestamp, setTimestamp] = useState('');
   const [notes, setNotes] = useState('');
   const [reasonId, setReasonId] = useState('');
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});
   const [generalError, setGeneralError] = useState(null);
   // Gate de freelance (handoff 0042 §6): quando o backend devolve 422
   // freelance_confirmation_required (item sem técnico atribuído), pedimos a
   // confirmação explícita e RE-ENVIAMOS a mesma requisição com
   // freelance_confirmed: true.
   const [freelanceRequired, setFreelanceRequired] = useState(false);

   const isCancelled = kindDef?.kind === 'CANCELLED';

   // Reseta o formulário a cada abertura — timestamp default = agora.
   useEffect(() => {
      if (!isOpen) return;
      setTimestamp(nowLocalInputValue());
      setNotes('');
      setReasonId('');
      setFieldErrors({});
      setGeneralError(null);
      setFreelanceRequired(false);
      setIsSubmitting(false);
   }, [isOpen]);

   if (!isOpen || !stop || !kindDef) return null;

   const handleConfirm = async () => {
      setFieldErrors({});
      setGeneralError(null);

      if (isCancelled && !reasonId) {
         setFieldErrors({
            non_completion_reason_id:
               'Obrigatório quando o evento é Não realizado.',
         });
         return;
      }

      const isoTimestamp = toIsoWithOffset(timestamp);
      if (!isoTimestamp) {
         setFieldErrors({ event_timestamp: 'Data/hora inválida.' });
         return;
      }

      setIsSubmitting(true);
      try {
         const payload = {
            event_kind: kindDef.kind,
            event_timestamp: isoTimestamp,
            notes: notes.trim() || null,
            non_completion_reason_id: isCancelled ? Number(reasonId) : null,
         };
         // Só carimba freelance após o operador confirmar (gate §6) — ausente =
         // não confirmado.
         if (freelanceRequired) payload.freelance_confirmed = true;
         await onSubmit(payload);
         // Sucesso: quem fecha o dialog (e dá o toast) é o StopDrawer.
      } catch (err) {
         const code = firstErrorCode(err);
         const apiMessage = err?.response?.data?.message;

         // Gate de freelance (§6): item sem técnico nenhum. Não é erro de
         // formulário — pedimos a confirmação e re-enviamos com
         // freelance_confirmed na próxima confirmação.
         if (code === 'freelance_confirmation_required') {
            setFreelanceRequired(true);
            setFieldErrors({});
            setGeneralError(null);
            setIsSubmitting(false);
            return;
         }
         // Rota com técnico designado mas NÃO atribuída: orientar a atribuir a
         // rota antes de concluir (não é freelance).
         if (code === 'route_not_assigned') {
            setFreelanceRequired(false);
            setFieldErrors({});
            setGeneralError(
               apiMessage ??
                  'Atribua a rota (botão “Atribuir rota”) antes de concluir o item.'
            );
            setIsSubmitting(false);
            return;
         }

         const mapped = mapFieldErrors(err);
         setFieldErrors(mapped);
         // Erros sem campo no formulário (event_kind, item_status,
         // assigned_technician_employee_id) viram aviso geral.
         const formFields = ['event_timestamp', 'non_completion_reason_id'];
         const offForm = Object.entries(mapped)
            .filter(([field]) => !formFields.includes(field))
            .map(([, message]) => message);
         if (offForm.length > 0) {
            setGeneralError(offForm.join(' '));
         } else if (Object.keys(mapped).length === 0) {
            setGeneralError(apiMessage ?? 'Erro ao registrar evento.');
         }
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={`Registrar evento — ${kindDef.label}`}
         maxWidth="480px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Voltar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting || (isCancelled && !reasonId)}
               >
                  {(() => {
                     if (isSubmitting) return 'Registrando…';
                     if (freelanceRequired) return 'Confirmar como freelance';
                     return 'Confirmar evento';
                  })()}
               </PrimaryButton>
            </>
         }
      >
         <Body>
            <SummaryLine>
               <strong>{kindDef.label}</strong> em <strong>{stop.nm}</strong> (
               {stop.id}). Verifique antes de confirmar — a correção de evento
               não está disponível na tela.
            </SummaryLine>

            {isTerminalOverride && (
               <OverrideBox>
                  Este item já está em estado terminal. Confirmar vai
                  sobrescrever o desfecho atual (ação de operador sênior).
               </OverrideBox>
            )}

            {freelanceRequired && (
               <OverrideBox>
                  A conclusão deste item sem técnico atribuído registrará o
                  atendimento como <strong>técnico freelance</strong>. Considere
                  renomear a rota com o nome do freelance para facilitar consultas
                  futuras. Clique em <strong>Confirmar como freelance</strong>{' '}
                  para prosseguir.
               </OverrideBox>
            )}

            {generalError && <WarnBox>{generalError}</WarnBox>}

            <Field>
               <label htmlFor="hf-event-timestamp">Data e hora do evento</label>
               <input
                  id="hf-event-timestamp"
                  type="datetime-local"
                  value={timestamp}
                  onChange={(e) => setTimestamp(e.target.value)}
                  disabled={isSubmitting}
               />
               {fieldErrors.event_timestamp && (
                  <FieldError>{fieldErrors.event_timestamp}</FieldError>
               )}
            </Field>

            {isCancelled && (
               <Field>
                  <NonCompletionReasonPicker
                     id="hf-event-reason"
                     labelText="Motivo de não realização"
                     required
                     value={reasonId}
                     onChange={setReasonId}
                     disabled={isSubmitting}
                  />
                  {fieldErrors.non_completion_reason_id && (
                     <FieldError>
                        {fieldErrors.non_completion_reason_id}
                     </FieldError>
                  )}
               </Field>
            )}

            <Field>
               <label htmlFor="hf-event-notes">Observações (opcional)</label>
               <textarea
                  id="hf-event-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Contexto do evento — visível na timeline do item."
                  disabled={isSubmitting}
               />
            </Field>
         </Body>
      </Modal>
   );
}

EventConfirmDialog.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   stop: PropTypes.object,
   kindDef: PropTypes.shape({
      kind: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
   }),
   isTerminalOverride: PropTypes.bool,
   onSubmit: PropTypes.func.isRequired,
};
